import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Search,
  Calendar,
  MessageSquare,
  User,
  Wallet,
  ShieldCheck,
  Heart,
  Sparkles,
  CheckCircle2,
  Clock,
  ArrowRight
} from 'lucide-react';
import { UserProfile } from '../types';

interface ClientDashboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  userProfile: UserProfile | null;
  onFindCompanion: () => void;
  onOpenBookings: () => void;
  onOpenProfile: () => void;
  onOpenWallet: () => void;
}

export const ClientDashboardModal: React.FC<ClientDashboardModalProps> = ({
  isOpen,
  onClose,
  userProfile,
  onFindCompanion,
  onOpenBookings,
  onOpenProfile,
  onOpenWallet,
}) => {
  const navigate = useNavigate();
  if (!isOpen) return null;

  const clientName = userProfile?.name || 'Valued Member';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/75 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 animate-in fade-in">
      <div className="relative bg-white rounded-3xl max-w-2xl w-full overflow-hidden shadow-2xl border border-pink-100 p-6 sm:p-8 max-h-[92vh] overflow-y-auto">
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors z-10 cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <img
            src={
              userProfile?.avatarUrl ||
              'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=150&q=80'
            }
            alt={clientName}
            className="w-16 h-16 rounded-2xl object-cover border-2 border-pink-200 shadow-sm"
          />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl sm:text-2xl font-black text-slate-900">
                Welcome, {userProfile?.displayName || clientName}! 👋
              </h2>
              {userProfile?.isIdVerified ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-sky-50 text-sky-700 border border-sky-200 text-xs font-bold">
                  <CheckCircle2 className="w-3.5 h-3.5 text-sky-600" />
                  <span>Verified ✓</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 text-xs font-bold">
                  <Clock className="w-3.5 h-3.5 text-amber-600" />
                  <span>KYC Pending</span>
                </span>
              )}
            </div>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200">
                FREE CLIENT ACCOUNT
              </span>
              <span className="text-xs text-slate-500">
                Public Name: <strong>{userProfile?.displayName || clientName}</strong>
              </span>
            </div>
          </div>
        </div>

        {/* Identity & Privacy Status Banner */}
        <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 mb-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${userProfile?.isIdVerified ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}`}>
              {userProfile?.isIdVerified ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-900">
                  {userProfile?.isIdVerified ? 'Government ID Verified ✓' : 'Government ID Verification Pending'}
                </span>
                <span className={`text-[10px] px-1.5 py-0.2 font-bold rounded ${userProfile?.isIdVerified ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                  {userProfile?.isIdVerified ? 'Verified' : 'In Review'}
                </span>
              </div>
              <p className="text-slate-500 text-[11px] mt-0.5">
                Others only see your profile name <strong>"{userProfile?.displayName || clientName}"</strong>. Your legal name{' '}
                <span className="font-medium text-slate-700">
                  ({userProfile?.legalName || userProfile?.clientProfile?.legalName || 'ID Verified'})
                </span>{' '}
                is kept strictly private.
              </p>
            </div>
          </div>
          <span className="text-[10px] text-slate-400 font-semibold shrink-0">
            🔒 Private & Compliant
          </span>
        </div>

        {/* Benefits reminder banner */}
        <div className="p-4 rounded-2xl bg-gradient-to-r from-pink-50 via-purple-50 to-pink-50 border border-pink-100 mb-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="text-xs">
            <strong className="text-slate-900 block font-bold">100% Free Client Access</strong>
            <span className="text-slate-600">
              Browse verified companions, submit booking requests, and enjoy secure escrow payments without any subscription fee.
            </span>
          </div>
        </div>

        {/* Quick Actions (Spec Section 2, Step 4) */}
        <div className="space-y-3 mb-6">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate('/companions');
                if (onFindCompanion) onFindCompanion();
              }}
              className="p-4 rounded-2xl border border-slate-200 hover:border-pink-300 hover:bg-pink-50/30 transition-all flex items-center justify-between group cursor-pointer text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Search className="w-5 h-5" />
                </div>
                <div>
                  <strong className="text-sm font-bold text-slate-900 block">Find a Companion</strong>
                  <span className="text-xs text-slate-500">Explore verified companions & partners</span>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-pink-600 group-hover:translate-x-1 transition-all" />
            </button>

            <button
              type="button"
              onClick={() => {
                onClose();
                navigate('/bookings');
                if (onOpenBookings) onOpenBookings();
              }}
              className="p-4 rounded-2xl border border-slate-200 hover:border-purple-300 hover:bg-purple-50/30 transition-all flex items-center justify-between group cursor-pointer text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <strong className="text-sm font-bold text-slate-900 block">View Bookings</strong>
                  <span className="text-xs text-slate-500">Check upcoming dates & active OTPs</span>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
            </button>

            <button
              type="button"
              onClick={() => {
                onClose();
                navigate('/profile');
                if (onOpenProfile) onOpenProfile();
              }}
              className="p-4 rounded-2xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 transition-all flex items-center justify-between group cursor-pointer text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <strong className="text-sm font-bold text-slate-900 block">My Profile</strong>
                  <span className="text-xs text-slate-500">Preferences & saved details</span>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
            </button>

            <button
              type="button"
              onClick={() => {
                onClose();
                navigate('/dashboard');
                if (onOpenWallet) onOpenWallet();
              }}
              className="p-4 rounded-2xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all flex items-center justify-between group cursor-pointer text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Wallet className="w-5 h-5" />
                </div>
                <div>
                  <strong className="text-sm font-bold text-slate-900 block">Wallet & Payments</strong>
                  <span className="text-xs text-slate-500">Receipts & escrow balances</span>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
            </button>

          </div>
        </div>

        {/* Safety & Support Card */}
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between text-xs text-slate-600">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>24/7 Concierge Support & 4-Digit Meeting OTP Protection active</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="font-bold text-pink-600 hover:text-pink-700 cursor-pointer shrink-0"
          >
            Start Browsing
          </button>
        </div>

      </div>
    </div>
  );
};
